import { hilRevisionStatusToBuildStatus } from '@cadstrom/backend/shared';
import { BuildStatus } from '@cadstrom/cadstrom-shared';

import { displayedRevisionStatusFor } from '#utils/revision-display-status.js';
import { getFlowPosition } from '#utils/revision-flow.js';

import type { HilProjectPreview } from '@cadstrom/backend/shared';
import type { BuildAutomationMode, RevisionStatus } from '@cadstrom/cadstrom-shared';

/**
 * Which display section a project lands in:
 *
 * - `needs`: the highlighted "Needs you" queue, where a design is waiting on the user.
 * - `all`: the catch-all "All projects" list, covering in progress, complete, and revision-less.
 * - `demo`: the separate "Demo projects" section, holding seeded example projects
 *   regardless of their action state.
 */
export type TriageGroupId = 'needs' | 'all' | 'demo';

/** A single project flattened into the fields the triage list renders. */
export interface ProjectTriageItem {
	id: string;
	name: string;
	latestRevisionId: string | undefined;
	latestRevisionName: string | undefined;
	status: RevisionStatus | undefined;
	hasUploadedFiles: boolean;
	automationMode: BuildAutomationMode | undefined;
	updatedAt: number | undefined;
	group: TriageGroupId;
	isDemo: boolean;
}

/** Projects split into the triage buckets, each sorted most-recent first. */
export interface TriagePartition {
	needs: ProjectTriageItem[];
	all: ProjectTriageItem[];
	demo: ProjectTriageItem[];
}

/**
 * Whether a design is waiting on the user, and so belongs in the highlighted
 * "Needs you" queue.
 *
 * @param status Latest-revision status, or undefined when the project has no revisions.
 * @param hasUploadedFiles Whether that revision has any source file.
 * @param automationMode That revision's automation mode.
 * @param showBillingFlow Whether the organization is on the billing flow; with the
 *   automation mode it decides whether the analysis gate is waiting on the customer.
 * @returns Whether the project needs the user's attention.
 */
export function needsAttention(
	status: RevisionStatus | undefined,
	hasUploadedFiles: boolean,
	automationMode: BuildAutomationMode | undefined,
	showBillingFlow: boolean,
): boolean {
	if (status === undefined) {
		return false;
	}

	return (
		status === BuildStatus.Error ||
		getFlowPosition({ status, hasUploadedFiles, automationMode, showBillingFlow }).tone === 'action'
	);
}

/**
 * Flatten a project preview into the triage row model, resolving its display
 * group.
 *
 * The preview reports `processing` for a revision that has not started, because
 * the customer-facing status has no `pending`; an absent upload recovers it, so
 * the list can tell a design waiting on the user from one being worked on.
 *
 * @param project Project preview from the projects list query.
 * @param showBillingFlow Whether the project's organization is on the billing flow.
 * @param isStaff Whether the viewer is staff, who see a failed revision as failed.
 * @returns The flattened triage item.
 */
export function toTriageItem(project: HilProjectPreview, showBillingFlow: boolean, isStaff = false): ProjectTriageItem {
	const { latestRevision, isDemo } = project;
	const hasUploadedFiles = latestRevision?.hasUploadedFiles ?? false;

	let status: RevisionStatus | undefined;
	if (latestRevision !== null) {
		const reported = displayedRevisionStatusFor(
			{
				status: hilRevisionStatusToBuildStatus(latestRevision.status),
				erroredFromStatus:
					latestRevision.erroredFromStatus === undefined ?
						undefined
					:	hilRevisionStatusToBuildStatus(latestRevision.erroredFromStatus),
			},
			{ isStaff },
		);
		status = reported === BuildStatus.Processing && !hasUploadedFiles ? BuildStatus.Pending : reported;
	}

	return {
		id: project.id,
		name: project.name,
		latestRevisionId: latestRevision?.id,
		latestRevisionName: latestRevision?.name,
		status,
		hasUploadedFiles,
		automationMode: latestRevision?.automationMode,
		updatedAt: project.updatedAt ?? undefined,
		// Demo projects are corralled into their own section regardless of status.
		group:
			isDemo ? 'demo'
			: needsAttention(status, hasUploadedFiles, latestRevision?.automationMode, showBillingFlow) ? 'needs'
			: 'all',
		isDemo,
	};
}

/**
 * Partition projects into the "Needs you", "All projects", and "Demo projects"
 * buckets, each sorted by last-updated descending so the freshest work sits at
 * the top of every group.
 *
 * @param projects Project previews to partition.
 * @param showBillingFlow Whether the projects' organization is on the billing flow.
 * @param isStaff Whether the viewer is staff, who see a failed revision as failed.
 * @returns The three buckets, each independently sorted most-recent first.
 */
export function partitionProjects(
	projects: HilProjectPreview[],
	showBillingFlow: boolean,
	isStaff = false,
): TriagePartition {
	const items = projects.map((project) => toTriageItem(project, showBillingFlow, isStaff)).sort(byUpdatedDescending);
	return {
		needs: items.filter((item) => item.group === 'needs'),
		all: items.filter((item) => item.group === 'all'),
		demo: items.filter((item) => item.group === 'demo'),
	};
}

/**
 * Comparator that orders triage items by last-updated time, most recent first.
 * Items without a timestamp sort last.
 */
function byUpdatedDescending(a: ProjectTriageItem, b: ProjectTriageItem): number {
	return (b.updatedAt ?? 0) - (a.updatedAt ?? 0);
}
